import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const ProfileFeatureWidgetActionMenu = ({ label = 'Widget actions', actions = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const visibleActions = actions.filter((action) => action && !action.hidden);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const updateMenuPosition = () => {
      const buttonRect = buttonRef.current?.getBoundingClientRect();
      if (!buttonRect) {
        return;
      }

      const menuWidth = 188;
      const viewportPadding = 10;
      const left = Math.min(
        Math.max(viewportPadding, buttonRect.right - menuWidth),
        window.innerWidth - menuWidth - viewportPadding
      );

      setMenuPosition({
        top: buttonRect.bottom + 8,
        left,
      });
    };

    const handlePointerDown = (event) => {
      if (
        buttonRef.current?.contains(event.target)
        || menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    updateMenuPosition();
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen]);

  if (visibleActions.length === 0) {
    return null;
  }

  const handleActionClick = (action) => {
    if (action.disabled) {
      return;
    }

    setIsOpen(false);
    if (typeof action.onClick === 'function') {
      action.onClick();
    }
  };

  const menu = isOpen
    ? ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="profile-feature-widget-action-menu-list"
          role="menu"
          style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
        >
          {visibleActions.map((action) => (
            action.href && !action.disabled ? (
              <a
                key={action.label}
                className="profile-feature-widget-action-menu-item"
                href={action.href}
                role="menuitem"
                onClick={() => setIsOpen(false)}
              >
                {action.label}
              </a>
            ) : (
              <button
                key={action.label}
                type="button"
                className="profile-feature-widget-action-menu-item"
                role="menuitem"
                disabled={Boolean(action.disabled)}
                onClick={() => handleActionClick(action)}
              >
                {action.label}
              </button>
            )
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div className="profile-feature-widget-action-menu">
      <button
        ref={buttonRef}
        type="button"
        className="profile-feature-widget-action-menu-button"
        aria-label={label}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((currentIsOpen) => !currentIsOpen)}
      >
        &#8942;
      </button>
      {menu}
    </div>
  );
};

export default ProfileFeatureWidgetActionMenu;